import React, { useCallback, useEffect, useRef, useState } from "react";
import { BodyText } from "../../../shared/text/BodyText.styled";
import TakeOffLoginShell from "../shared/TakeOffLoginShell";
import { useBanner } from "../../../context/BannerContext";
import { track } from "../../../../clients/mixpanel";
import { useTakeOffLoginActions } from "../../../hooks/useTakeOffLoginActions";
import { useAto } from "./AtoContext";
import {
  buildAuthorizeUrl,
  clearOAuthSessionMarkers,
  validateOAuthState,
} from "../../../../integrations/qto/sources/ato/auth";
import { ATO_SIGN_IN_CANCELLED, ATO_SIGN_IN_INVALID_RESPONSE, ATO_SIGN_IN_LABEL } from "../../../constants";

interface OAuthResult {
  type?: string;
  code?: string | null;
  state?: string | null;
  error?: string | null;
  error_description?: string | null;
}

/** Office dialog code for "the user closed the dialog with the X button" — a cancellation, not a failure. */
const DIALOG_CLOSED_BY_USER = 12006;

const DIALOG_ERROR_MESSAGES: Record<number, string> = {
  12002: "Could not load the Autodesk sign-in page",
  12003: "Could not open the sign-in dialog",
  12004: "Could not open the sign-in dialog",
  12005: "Could not open the sign-in dialog",
  12007: "A sign-in window is already open",
  12009: "Allow the sign-in window to open and try again",
};

const DIALOG_ERROR_FALLBACK = "Autodesk sign-in failed";

const dialogErrorMessage = (code?: number): string =>
  (code !== undefined && DIALOG_ERROR_MESSAGES[code]) || DIALOG_ERROR_FALLBACK;

/**
 * Autodesk signs in through an Office dialog running the OAuth authorize flow. The result arrives
 * either as a dialog message or, as a fallback, a `window.postMessage` from ato-redirect.html.
 */
const AtoSignInButton: React.FC = () => {
  const { showBanner } = useBanner();
  const { initializeAtoState } = useAto();
  const { handleAtoTokenExchange, logAtoDialogFailure } = useTakeOffLoginActions();
  const [busy, setBusy] = useState<boolean>(false);

  // Guards against the Office dialog and the window.postMessage fallback in ato-redirect.html both
  // resolving the same sign-in attempt.
  const oauthSettled = useRef<boolean>(false);

  const handleOAuthResult = useCallback(
    async (d: OAuthResult) => {
      if (oauthSettled.current) return;
      oauthSettled.current = true;

      if (d.error) {
        showBanner(d.error_description || d.error || "Autodesk sign-in failed", "error");
        clearOAuthSessionMarkers();
        setBusy(false);
        return;
      }
      if (!d.code || !validateOAuthState(d.state ?? null)) {
        showBanner(ATO_SIGN_IN_CANCELLED, "error");
        clearOAuthSessionMarkers();
        setBusy(false);
        return;
      }
      try {
        await handleAtoTokenExchange(d.code);
        void track("excel.qto.ato.login");
        await initializeAtoState();
      } catch (e) {
        showBanner(e instanceof Error ? e.message : "Token exchange failed", "error");
      } finally {
        setBusy(false);
      }
    },
    [handleAtoTokenExchange, initializeAtoState, showBanner]
  );

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as OAuthResult;
      if (!d || d.type !== "ediphi-ato-oauth") return;
      void handleOAuthResult(d);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleOAuthResult]);

  const handleSignIn = async () => {
    oauthSettled.current = false;
    setBusy(true);
    try {
      const { authorizeUrl } = await buildAuthorizeUrl();
      const dialogUrl = `${window.location.origin}/ato-redirect.html?launchUrl=${encodeURIComponent(authorizeUrl)}`;

      Office.context.ui.displayDialogAsync(dialogUrl, { width: 45, height: 65 }, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          const code = asyncResult.error?.code;
          oauthSettled.current = true;
          logAtoDialogFailure(asyncResult.error?.message || "displayDialogAsync failed", code);
          showBanner(dialogErrorMessage(code), "error");
          clearOAuthSessionMarkers();
          setBusy(false);
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: { message: string } | { error: number }) => {
            dialog.close();
            if ("message" in arg) {
              try {
                const d = JSON.parse(arg.message) as OAuthResult;
                void handleOAuthResult(d);
              } catch {
                oauthSettled.current = true;
                showBanner(ATO_SIGN_IN_INVALID_RESPONSE, "error");
                clearOAuthSessionMarkers();
                setBusy(false);
              }
            }
          }
        );

        // Fires when the user closes the dialog with the X button, or on navigation errors. A
        // programmatic dialog.close() does not fire it, so this only ever runs for outcomes the
        // user never completed.
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: { message: string } | { error: number }) => {
          if (oauthSettled.current) return;
          oauthSettled.current = true;

          const code = "error" in arg ? arg.error : undefined;

          clearOAuthSessionMarkers();
          setBusy(false);

          // Closing the dialog is a deliberate back-out, not a failure: reset to the signed-out
          // form silently rather than telling the user something went wrong.
          if (code === DIALOG_CLOSED_BY_USER) return;

          logAtoDialogFailure("Autodesk sign-in dialog closed unexpectedly", code);
          showBanner(dialogErrorMessage(code), "error");
        });
      });
    } catch (e) {
      oauthSettled.current = true;
      logAtoDialogFailure(e instanceof Error ? e.message : "Could not start Autodesk sign-in");
      showBanner(e instanceof Error ? e.message : "Could not start Autodesk sign-in", "error");
      setBusy(false);
    }
  };

  return (
    <TakeOffLoginShell
      sourceId="ato"
      submitLabel={ATO_SIGN_IN_LABEL}
      busy={busy}
      submitDisabled={busy}
      onSubmit={() => void handleSignIn()}
    >
      <BodyText sx={{ pb: "24px" }}>
        Sign in with your Autodesk account to load Takeoff packages and quantities.
      </BodyText>
    </TakeOffLoginShell>
  );
};

export default AtoSignInButton;
